import { useEffect, useRef, useState } from 'react';
import { useLatestRef } from './useLatestRef';

export interface UseBlurCommitOptions<T extends object> {
  value?: T | null;
  onChange?: (value: T | null) => void;
  options: T[];
  getKey: (option: T) => string;
  getOptionLabel: (option: T) => string;
  suggestedValue?: T;
  onInputChangeProp?: (value: string) => void;
}

export interface BlurCommit<T extends object> {
  // State
  inputValue: string;
  isFocused: boolean;
  isSearching: boolean;
  // Refs
  inputRef: React.RefObject<HTMLInputElement>;
  inputValueRef: React.MutableRefObject<string>;
  /** An option accepted by Tab, applied on blur rather than on the keydown. See `commitBlur`. */
  pendingCommitRef: React.MutableRefObject<T | null>;
  // Handlers
  handleSelectionChange: (key: React.Key | null) => void;
  handleInputChange: (newValue: string) => void;
  handleClear: () => void;
  onFocusChange: (focused: boolean) => void;
}

export function useBlurCommit<T extends object>({
  value,
  onChange,
  options,
  getKey,
  getOptionLabel,
  suggestedValue,
  onInputChangeProp,
}: UseBlurCommitOptions<T>): BlurCommit<T> {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingCommitRef = useRef<T | null>(null);
  // `handleClear` focuses the input on its way out, and that focus lands before the parent has
  // re-rendered with the cleared value — so the focus handler would read the OLD value and put the
  // label straight back. This says "the field was just emptied on purpose".
  const justClearedRef = useRef(false);
  // Latest-value refs so the deferred blur handler and the Enter-capture handler
  // read current state instead of a stale closure.
  const valueRef = useLatestRef(value);
  const inputValueRef = useLatestRef(inputValue);

  // Cleanup blur timeout on unmount
  useEffect(() => () => clearTimeout(blurTimeoutRef.current), []);

  const cancelPendingBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = undefined;
    }
  };

  // Handle selection change (fired when user clicks a list item)
  const handleSelectionChange = (key: React.Key | null) => {
    if (key === null) {
      // Only clear if there's actually a value to clear, to avoid infinite re-render loops
      if (value) {
        onChange?.(null);
        setInputValue('');
      }
      return;
    }

    const selected = (options ?? []).find((option) => getKey(option) === key);
    if (selected) {
      justClearedRef.current = false;
      onChange?.(selected);
      setInputValue(getOptionLabel(selected));
      setIsSearching(false);
    }
  };

  // Handle input change
  const handleInputChange = (newValue: string) => {
    justClearedRef.current = false;
    // If the user is still typing, cancel any pending blur — the input still has focus
    cancelPendingBlur();
    setIsFocused(true);
    setInputValue(newValue);
    setIsSearching(true);

    onInputChangeProp?.(newValue);
  };

  // Handle clear
  const handleClear = () => {
    justClearedRef.current = true;
    pendingCommitRef.current = null;
    onChange?.(null);
    setInputValue('');
    onInputChangeProp?.('');
    inputRef.current?.focus();
  };

  // Reconcile the input text with committed state once a blur is confirmed real. Reads latest-value refs (not the closure) because it runs after a 0ms defer. Steps are mutually exclusive, ordered most- to least-specific.
  const commitBlur = () => {
    const committedValue = valueRef.current;
    const typedText = inputValueRef.current;
    setIsFocused(false);
    setIsSearching(false);

    // Commit on blur, NOT on the keydown: writing the selection while the input still holds focus re-opens the listbox behind the next field — react-aria reopens on an input-value change, after its own close-on-blur has already run.
    const pending = pendingCommitRef.current;

    if (pending) {
      pendingCommitRef.current = null;
      onChange?.(pending);
      setInputValue(getOptionLabel(pending));

      return;
    }

    // 1. Nothing committed, but the typed text matches the suggestion: adopt it.
    if (!committedValue && suggestedValue && typedText === getOptionLabel(suggestedValue)) {
      onChange?.(suggestedValue);
      setInputValue(getOptionLabel(suggestedValue));
      return;
    }

    // 2. A value was committed but the user emptied the input: clear it.
    if (committedValue && !typedText.trim()) {
      onChange?.(null);
      setInputValue('');
      return;
    }

    // 3. Otherwise snap the input back to the committed value's label (or empty).
    setInputValue(committedValue ? getOptionLabel(committedValue) : '');
  };

  const onFocusChange = (focused: boolean) => {
    if (focused) {
      // A pending blur timeout means this focus is a popover-related refocus
      // (focus cycled out then back in), not a fresh initial focus.
      const isRefocus = !!blurTimeoutRef.current;
      cancelPendingBlur();
      setIsFocused(true);
      if (!isRefocus) {
        // True initial focus: populate input with current value — unless the field was just
        // cleared, in which case `value` is the label the user asked us to remove.
        setIsSearching(false);
        setInputValue(!justClearedRef.current && value ? getOptionLabel(value) : '');
        justClearedRef.current = false;
      }
      return;
    }

    // Defer the blur to distinguish a real blur from popover-related focus
    // cycling — a refocus within the same tick cancels this before it runs.
    blurTimeoutRef.current = setTimeout(() => {
      blurTimeoutRef.current = undefined;
      commitBlur();
    }, 0);
  };

  return {
    inputValue,
    isFocused,
    isSearching,
    inputRef,
    inputValueRef,
    pendingCommitRef,
    handleSelectionChange,
    handleInputChange,
    handleClear,
    onFocusChange,
  };
}
